import React from 'react';
import { FormattedMessage } from 'react-intl';
import { EvaluationLevel } from './validatePassword';

type IndicatorProps = {
  level: EvaluationLevel;
  id?: string;
};

const strengthMessages: Record<
  EvaluationLevel,
  { id: string; defaultMessage: string; className: string }
> = {
  [EvaluationLevel.INVALID]: {
    id: 'passwordstrength.invalid',
    defaultMessage: 'ungültig',
    className: 'text-color-error-dark',
  },
  [EvaluationLevel.TOO_SHORT]: {
    id: 'passwordstrength.tooshort',
    defaultMessage: 'zu kurz',
    className: 'text-color-error-dark',
  },
  [EvaluationLevel.TOO_LONG]: {
    id: 'passwordstrength.toolong',
    defaultMessage: 'zu lang',
    className: 'text-color-error-dark',
  },
  [EvaluationLevel.WEAK]: {
    id: 'passwordstrength.weak',
    defaultMessage: 'schwach',
    className: 'text-color-error-dark',
  },
  [EvaluationLevel.MEDIUM]: {
    id: 'passwordstrength.medium',
    defaultMessage: 'mittel',
    className: 'text-color-warning-dark',
  },
  [EvaluationLevel.STRONG]: {
    id: 'passwordstrength.strong',
    defaultMessage: 'stark',
    className: 'text-color-success-dark',
  },
};

const indicatorColorMap = {
  [EvaluationLevel.INVALID]: 'bg-color-grey',
  [EvaluationLevel.WEAK]: 'bg-color-error',
  [EvaluationLevel.MEDIUM]: 'bg-color-warning',
  [EvaluationLevel.STRONG]: 'bg-color-success',
  [EvaluationLevel.TOO_LONG]: 'bg-color-grey',
  [EvaluationLevel.TOO_SHORT]: 'bg-color-grey',
};

const Indicator = ({ level, id: _id }: IndicatorProps) => {
  const bgColor = indicatorColorMap[level] || indicatorColorMap[EvaluationLevel.INVALID];
  return <div className={`h-0.5 w-full rounded transition-all duration-1000 ${bgColor}`} />;
};

const FirstIndicator: React.FC<{ level: EvaluationLevel }> = ({ level }) => {
  let id: string = level;
  let firstLevel: EvaluationLevel = level;
  if (![EvaluationLevel.MEDIUM, EvaluationLevel.STRONG, EvaluationLevel.WEAK].includes(level)) {
    id = 'default';
    firstLevel = EvaluationLevel.INVALID;
  }

  return <Indicator level={firstLevel} id={`first-${id}`} />;
};

const SecondIndicator = ({ level }: { level: EvaluationLevel }) => {
  let id: string = level;
  let secondLevel: EvaluationLevel = level;
  if (![EvaluationLevel.MEDIUM, EvaluationLevel.STRONG].includes(level)) {
    id = 'default';
    secondLevel = EvaluationLevel.INVALID;
  }

  return <Indicator level={secondLevel} id={`second-${id}`} />;
};

const ThirdIndicator = ({ level }: { level: EvaluationLevel }) =>
  level === EvaluationLevel.STRONG ? (
    <Indicator level={EvaluationLevel.STRONG} id="third-strong" />
  ) : (
    <Indicator level={EvaluationLevel.INVALID} id="third-default" />
  );

const PasswordStrengthText: React.FC<{ level: EvaluationLevel }> = ({ level }) => {
  const message = strengthMessages[level];
  if (!message) return null;
  return (
    <span className={`font-bold ${message.className}`}>
      <FormattedMessage id={message.id} defaultMessage={message.defaultMessage} />
    </span>
  );
};

export const PasswordStrengthIndicator = ({ level }: { level: EvaluationLevel }) => (
  <div className="mt-1 space-y-1 text-center">
    <div className="grid grid-cols-3 items-center justify-center gap-0.5">
      <FirstIndicator level={level} />
      <SecondIndicator level={level} />
      <ThirdIndicator level={level} />
    </div>
    <div className="text-size-body-xs text-start">
      <FormattedMessage id="passwordstrength.prefix" defaultMessage="Passwortstärke: " />
      <PasswordStrengthText level={level} />
    </div>
  </div>
);
